"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSimpleCodeGeneration = void 0;
const react_1 = require("react");
const generateCode_1 = require("../components/generateCode");
const useSimpleCodeGeneration = (options) => {
    const { userId, onCodeGenerated, onError, onStatusUpdate, conversation } = options;
    const [isGenerating, setIsGenerating] = (0, react_1.useState)(false);
    const [currentCode, setCurrentCode] = (0, react_1.useState)("");
    const [status, setStatus] = (0, react_1.useState)("");
    const wsRef = (0, react_1.useRef)(null);
    const currentCodeRef = (0, react_1.useRef)(""); // Use ref to track current code
    // Generate code from image and text
    const generateCodeFromInput = (0, react_1.useCallback)(({ imageUrl, fileType, text, isSelectionMode = false, attachments, }) => {
        console.log("🚀 Starting code generation with:", {
            imageUrl,
            fileType,
            text,
            userId,
        });
        // No image required; allow text-only generation
        setIsGenerating(true);
        setStatus("Starting code generation...");
        setCurrentCode("");
        currentCodeRef.current = ""; // Reset ref
        let chat_history;
        if (!isSelectionMode) {
            // ✅ Build chat history from latest 5 conversation pairs
            chat_history = (conversation !== null && conversation !== void 0 ? conversation : []).slice(-5).flatMap((pair) => [
                { role: "user", content: pair.prompt || "" },
                { role: "assistant", content: pair.aiResponseHtml || "" },
            ]);
            if (text) {
                chat_history.push({ role: "user", content: text });
            }
        }
        const params = {
            generationType: "create",
            generatedCodeConfig: "html_css",
            userId,
            textInput: text,
        };
        if (attachments && attachments.length > 0) {
            params.attachments = attachments; // 👈 multiple files
        }
        else {
            params.imageUrl = imageUrl;
            params.fileType = fileType;
        }
        if (chat_history) {
            params.chat_history = chat_history;
        }
        console.log("📤 Sending params to generateCode:", params);
        (0, generateCode_1.generateCode)(wsRef, params, 
        // On change - append code
        (token, variantIndex) => {
            console.log("📝 Received token:", token, "for variant:", variantIndex);
            // Track both variant 0 and 1, but prefer variant 1 if available
            if (variantIndex === 0 || variantIndex === 1) {
                setCurrentCode((prev) => {
                    const newCode = prev + token;
                    currentCodeRef.current = newCode; // Update ref
                    console.log("📄 Updated code length:", newCode.length);
                    return newCode;
                });
            }
        }, 
        // On set code - replace code
        (code, variantIndex) => {
            console.log("📋 Received full code for variant:", variantIndex, "length:", code.length);
            // Accept code from either variant 0 or 1, but prefer variant 1
            if (variantIndex === 0 || variantIndex === 1) {
                console.log("💾 Setting code from variant:", variantIndex);
                setCurrentCode(code);
                currentCodeRef.current = code; // Update ref
            }
        }, 
        // On status update
        (statusLine, variantIndex) => {
            console.log("📊 Status update:", statusLine, "for variant:", variantIndex);
            // Accept status from either variant
            if (variantIndex === 0 || variantIndex === 1) {
                setStatus(statusLine);
                onStatusUpdate === null || onStatusUpdate === void 0 ? void 0 : onStatusUpdate(statusLine);
            }
        }, 
        // On cancel
        () => {
            console.log("❌ Code generation cancelled");
            setIsGenerating(false);
            setStatus("Code generation cancelled");
            onError === null || onError === void 0 ? void 0 : onError("Code generation was cancelled");
        }, 
        // On complete
        () => {
            const finalCode = currentCodeRef.current;
            console.log("✅ Code generation completed. Final code length:", finalCode.length);
            console.log("📄 Final code preview:", finalCode.substring(0, 100) + "...");
            setIsGenerating(false);
            setStatus("Code generation completed");
            onCodeGenerated === null || onCodeGenerated === void 0 ? void 0 : onCodeGenerated(finalCode);
        });
    }, [userId, onCodeGenerated, onError, onStatusUpdate, conversation]);
    // Cancel generation
    const cancelGeneration = (0, react_1.useCallback)(() => {
        console.log("🛑 Cancelling generation");
        if (wsRef.current) {
            wsRef.current.close(4333); // USER_CLOSE_WEB_SOCKET_CODE
        }
    }, []);
    // Reset state
    const reset = (0, react_1.useCallback)(() => {
        console.log("🔄 Resetting code generation state");
        setIsGenerating(false);
        setCurrentCode("");
        currentCodeRef.current = "";
        setStatus("");
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);
    return {
        // State
        isGenerating,
        currentCode,
        status,
        // Actions
        generateCodeFromInput,
        cancelGeneration,
        reset,
    };
};
exports.useSimpleCodeGeneration = useSimpleCodeGeneration;
